<script setup lang="ts">
import { ref, computed, watch, type ComputedRef, unref } from 'vue';
import { isFunction } from 'lodash-es';
import { BSpinner } from 'bootstrap-vue-next';
import type { SpinnerType } from 'bootstrap-vue-next';
import * as downloads from '@integro/core/downloads';
import { useAuthDownload } from '../../composables';

interface FormInstance {
  readonly id: string;
  readonly name: string;
  submittable: ComputedRef<boolean>;
  disabled: ComputedRef<boolean>;
  readonly: ComputedRef<boolean>;
  reset(): void;
  handleSubmit(): Promise<void> | void;
  validate?(): Promise<boolean>;
}

const props = withDefaults(
  defineProps<{
    id?: string;
    dropdownItem?: boolean;
    form?: string | FormInstance;
    text?: string;
    tooltip?: string | null;
    icon?: string;
    iconAppend?: string;
    iconClass?: string | object;
    spinText?: string;
    spinning?: boolean;
    spinnerType?: SpinnerType;
    type?: string;
    size?: string;
    variant?: string;
    pill?: boolean;
    squared?: boolean;
    pressed?: boolean;
    disabled?: boolean;
    block?: boolean;
    noWrap?: boolean;
    noTab?: boolean;
    href?: string | (() => string);
    useAuth?: boolean;
    showSpinner?: boolean;
    forceDownload?: boolean;
    downloadFileName?: string | null;
  }>(),
  {
    dropdownItem: false,
    tooltip: null,
    spinning: false,
    spinnerType: 'grow',
    type: 'button',
    disabled: false,
    block: false,
    noWrap: true,
    noTab: false,
    useAuth: false,
    showSpinner: false,
    forceDownload: false,
    downloadFileName: null,
  },
);
const emit = defineEmits(['click', 'file']);

const isObservedForm = (form: any): form is FormInstance => {
  return form && typeof form.handleSubmit === 'function';
};

const { downloading, openFile, saveFile } = useAuthDownload();
const isSpinning = computed(
  () => props.spinning || (props.showSpinner && downloading.value),
);
const formInstance = ref<FormInstance | null>(null);

watch(
  () => props.form,
  (newValue) => {
    formInstance.value = isObservedForm(newValue) ? newValue : null;
  },
  { immediate: true },
);

const btnVariant = computed(() => {
  return (
    props.variant ||
    (props.type === 'submit'
      ? 'primary'
      : props.dropdownItem
        ? undefined
        : 'light')
  );
});

const tagName = computed(() => {
  return props.dropdownItem ? 'BDropdownItem' : 'BButton';
});

const formID = computed(() => {
  if (isDisabled.value) {
    return undefined;
  }
  if (formInstance.value) {
    return formInstance.value.id;
  }
  return typeof props.form === 'string' ? props.form : undefined;
});

const className = computed(() => {
  return {
    'itp-button': true,
    'text-nowrap': props.noWrap,
    border: btnVariant.value === 'light' && !props.dropdownItem,
  };
});

const isDisabled = computed(() => {
  if (props.type === 'submit' && formInstance.value) {
    const isSubmittable = unref(formInstance.value.submittable);
    return props.disabled || isSpinning.value || !isSubmittable;
  }
  return props.disabled || isSpinning.value;
});

const isFaIcon = (icon: string | undefined) => {
  if (!icon) {
    return false;
  }
  return icon.startsWith('fa-') || icon.startsWith('fa:');
};

const isIconify = (icon: string | undefined) => {
  if (!icon) {
    return false;
  }
  return icon.includes(':') && !icon.startsWith('fa:');
};

const faClassName = (icon: string | undefined) => {
  if (!icon) {
    return null;
  }
  if (icon.startsWith('fa:')) {
    return `fas fa-${icon.substring(3)}`;
  }
  if (icon.startsWith('fa-')) {
    return `fas ${icon}`;
  }
  return null;
};

const formIcon = computed<string | undefined>(() => {
  if (isSpinning.value || props.icon) {
    return undefined;
  }
  if (formInstance.value) {
    return 'material-symbols:done';
  }
  return undefined;
});

const iconifyIcon = computed(() => {
  if (isIconify(props.icon)) {
    return props.icon;
  }
  return formIcon.value;
});

const faIconClass = computed(() => {
  return faClassName(props.icon);
});

const iconifyIconAppend = computed(() => {
  return isIconify(props.iconAppend) ? props.iconAppend : undefined;
});

const faIconAppendClass = computed(() => {
  return faClassName(props.iconAppend);
});

const getHref = () => (isFunction(props.href) ? props.href() : props.href);
const getURL = () => {
  const href = getHref();
  return href ? downloads.normalizeLink(href) : null;
};

const effectiveType = computed(() => {
  if (isDisabled.value && props.type === 'submit') {
    return 'button';
  }
  return props.type;
});

const onClick = (ev: UIEvent) => {
  if (isDisabled.value) {
    ev.preventDefault();
    ev.stopPropagation();

    if (props.type === 'submit' && formInstance.value?.validate) {
      formInstance.value.validate();
    }
    return;
  }

  if ((props.href && props.useAuth) || (props.href && props.forceDownload)) {
    ev.preventDefault();
  }

  if (props.type === 'submit' && formInstance.value) {
    if (typeof formInstance.value.handleSubmit === 'function') {
      if (!props.href) {
        ev.preventDefault();
      }
      formInstance.value.handleSubmit();
    }
  }

  const url = getURL();
  if ((props.useAuth || props.forceDownload) && url) {
    const download = props.forceDownload ? saveFile : openFile;
    void download(url, props.downloadFileName).then(
      (file) => file && emit('file', file),
    );
    return;
  }

  emit('click', ev);
};
</script>

<template>
  <component
    :is="tagName"
    :id="id"
    :form="formID"
    :class="className"
    :variant="btnVariant"
    :size="size"
    :block="block"
    :disabled="isDisabled"
    :pill="pill"
    :squared="squared"
    :pressed="pressed === true ? true : undefined"
    :title="tooltip"
    :type="effectiveType"
    :href="href"
    :tabindex="noTab ? '-1' : undefined"
    @click="onClick"
  >
    <BSpinner v-if="isSpinning" small class="me-1" :type="spinnerType" />

    <iconify-icon
      v-if="iconifyIcon && (!isSpinning || spinText)"
      noobserver
      :icon="iconifyIcon"
      :class="[iconClass, text ? 'me-2' : '']"
    />
    <i
      v-else-if="faIconClass && (!isSpinning || spinText)"
      :class="[faIconClass, iconClass, text ? 'me-2' : '']"
    />

    <span v-if="isSpinning && spinText">{{ spinText }}</span>
    <span v-else>{{ text }}</span>
    <iconify-icon
      v-if="iconifyIconAppend && (!isSpinning || spinText)"
      noobserver
      :icon="iconifyIconAppend"
      class="ms-1"
      :class="iconClass"
    />
    <i
      v-else-if="faIconAppendClass && (!isSpinning || spinText)"
      :class="[faIconAppendClass, iconClass, 'ms-1']"
    />

    <slot />
  </component>
</template>

<style scoped lang="scss">
.itp-button {
  &.btn-link {
    padding: 0;
    margin: 0 0.5rem;
    color: #495057;
  }

  svg {
    font-size: 1.5rem;
  }

  &.btn-sm {
    svg {
      // font-size: 1.25rem;
    }
  }

  & iconify-icon {
    font-size: 1em;
    vertical-align: middle;
    line-height: 1;
  }
}

.btn-default-bordered {
  color: #333;
  background-color: #fff;
  border-color: #ccc;

  &:focus {
    color: #333;
    background-color: #e6e6e6;
    border-color: #8c8c8c;
  }
  &:hover {
    color: #333;
    background-color: #e6e6e6;
    border-color: #adadad;
  }
  &:active {
    color: #333;
    background-color: #e6e6e6;
    border-color: #adadad;
  }
}

.btn-default-borderless {
  color: #333;
  background-color: #fff;
  border: none;

  &:focus {
    color: #333;
    background-color: #e6e6e6;
    border: none;
  }
  &:hover {
    color: #333;
    background-color: #e6e6e6;
    border: none;
  }
  &:active {
    color: #333;
    background-color: #e6e6e6;
    border: none;
  }
}
</style>
